import {
  SocketInitializedPayload,
  SocketInitializedTypes,
} from '@act/shared/socket-events';
import {
  UnknownAction,
  PayloadAction,
  ThunkDispatch,
  createSlice,
} from '@reduxjs/toolkit';

import { appNotificationWebsocketActions } from './appNotificationSlice.slice';
import { callsSliceWebsocketActions } from './callsSlice.slice';
import { docketSliceWebsocketActions } from './docketSlice.slice';
import { enrollmentCaseloadWebsocketActions } from './enrollmentCaseloadSlice.slice';
import { feedSliceWebsocketActions } from './feedSlice.slice';
import { goalsTrackingWebsocketActions } from './goalsTracking.slice';
import { guideTargetsSliceWebsocketActions } from './guideTargets.slice';
import { hospitalizationsSliceWebsocketActions } from './hospitalizations.slice';
import { memberWebsocketActions } from './memberSlice.slice';
import { needSliceWebsocketActions } from './needs.slice';
import { resourcingSliceWebsocketActions } from './resourcing.slice';
import { scheduledEventWebsocketActions } from './scheduledEventSlice.slice';
import { surveySliceWebsocketActions } from './surveys.slice';
import { todoSliceWebsocketActions } from './todoSlice.slice';
import { resetRedux } from '../actions/sharedActions';
import { addToEffectsMiddleware } from 'redux/utils/effectsMiddleware.util';
import { v4 as generateUuid } from 'uuid';
import { SocketChannelTypes, actSocket } from '../websockets/actSocket';
import { verifyUser } from './actingUserSlice.slice';
import { usersSlice } from './usersSlice.slice';
import { WebsocketEventActions } from '../types/websocketTypes';

// THUNKS/ACTIONS ==============================================================

// INITIAL STATE ===============================================================
export interface SocketState {
  status: 'initial' | 'connecting' | 'connected' | 'disconnected';
  initializedId: string;
  hasHadDisconnect: boolean;
  socketName: string;
}
export interface WebsocketState {
  status: 'initial' | 'initialized';
  deviceId: string;
  baseSocket: SocketState;
}

export type { WebsocketEventActions };

const initialSocketState: SocketState = {
  status: 'initial',
  hasHadDisconnect: false,
  initializedId: null,
  socketName: null,
};

const initialState: WebsocketState = {
  status: 'initial',
  baseSocket: initialSocketState,
  deviceId: generateUuid(),
};

// SLICE =======================================================================

/**
 * Registers websocket listeners and dispatches corresponding actions to other
 * slices. API Event listener
 *
 * @param dispatch - The dispatch function from the Redux store.
 * @param websocketListeners - An array of websocket event actions to register.
 */
const registerWebsocketListeners = (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  websocketListeners: WebsocketEventActions[]
) => {
  for (const listener of websocketListeners) {
    actSocket.registerListener(
      listener.type,
      data => {
        const action: { type: string; payload?: object } = {
          type: listener.action.type,
        };
        action.payload = data;
        dispatch(action);
      },
      listener.channel
    );
  }
};
export const websocketSlice = createSlice({
  name: 'websocket',
  initialState: initialState,
  reducers: {
    connectionEstablished: (
      state,
      action: PayloadAction<SocketChannelTypes>
    ) => {
      state[action.payload].status = 'connected';
      state[action.payload].socketName = 'base';
      return state;
    },
    connectionLost: (state, action: PayloadAction<SocketChannelTypes>) => {
      state[action.payload].status = 'disconnected';
      state[action.payload].hasHadDisconnect = true;
      return state;
    },
    initialized: (state, action: PayloadAction<SocketInitializedPayload>) => {
      state['baseSocket'].initializedId = action.payload.id;
      return state;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        actSocket.disconnect();
        state = { ...initialState };
        return state;
      })
      .addCase(usersSlice.actions.userAuthenticated, state => {
        state.status = 'initialized';
      });
  },
});

// REDUCER =====================================================================
export const websocketReducer = websocketSlice.reducer;

// SELECTORS ===================================================================
export const selectWebsocketState = state =>
  (state as { websocket: typeof initialState })?.websocket;

// EXPORT HELPER ===============================================================

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: verifyUser.fulfilled,
    effect: async ({ state }) => {
      const websocketState = selectWebsocketState(state);
      if (websocketState.status !== 'initial') return;
      void actSocket.connect();
    },
  });
  addEffect({
    action: websocketSlice.actions.connectionEstablished,
    effect: async ({ dispatch }) => {
      registerWebsocketListeners(dispatch, [
        {
          type: SocketInitializedTypes.SOCKET_INITIALIZED,
          action: websocketSlice.actions.initialized,
          channel: 'baseSocket',
        },
        ...feedSliceWebsocketActions,
        ...goalsTrackingWebsocketActions,
        ...guideTargetsSliceWebsocketActions,
        ...appNotificationWebsocketActions,
        ...scheduledEventWebsocketActions,
        ...todoSliceWebsocketActions,
        ...docketSliceWebsocketActions,
        ...needSliceWebsocketActions,
        ...resourcingSliceWebsocketActions,
        ...memberWebsocketActions,
        ...enrollmentCaseloadWebsocketActions,
        ...surveySliceWebsocketActions,
        ...hospitalizationsSliceWebsocketActions,
        ...callsSliceWebsocketActions,
      ]);
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
// No websocket actions of websockets. That would be pretty meta.
